<!--
  @fileoverview Message Dispatcher Component - Message Type Router (Vue 3)

  This is the central message routing component that determines which
  specialized message component should render based on message content type.
  It acts as a dispatcher/factory for the various message formats supported
  by the webchat widget.

  @component Message
  @displayName Message Type Dispatcher

  Message Types Supported:
  - Text bubbles (MessageBubble) - Plain text with optional buttons
  - Files/Images (MessageFile) - File attachments, images, videos, audio
  - Carousel (MessageCarousel) - Horizontal scrollable cards
  - Compact (MessageCompact) - Vertically stacked cards
  - Grid (MessageGrid) - Grid layout for cards (beta feature)
  - Slider (MessageSlider) - Slider/pagination layout (beta feature)
  - Quick Replies (MessageQuickReplies) - Tappable suggestion buttons
  - SingPass (MessageSingPass) - Singapore NDI authentication QR
  - Scribe (MessageScribe) - AI scribe/transcription interface

  @example
  <Message
    :message="messageObject"
    :show-quick-reply="isLastMessage"
    :agents="agentsList"
    :message-index="index"
    @sendPostback="handlePostback"
    @setLightBoxImage="openLightbox"
    @handleExpandVideo="expandVideo"
  />
-->
<template>
  <!-- Fade transition for smooth message appearance -->
  <transition name="el-fade-in">
    <!-- Main message container - only renders if message has content -->
    <div
      v-if="hasAnyContent"
      class="block"
      :style="{ '--banner-offset': maintainBannerHeight + 'px' }"
      :class="{
        'has-banner-offset': maintainBannerHeight > 0 && messageIndex === 0,
        [stickyMenuClass]: stickyMenuClass,
        'message-container': true,
        'user-message': isUserMessage,
        'text-right': isUserMessage,
        'text-left': !isUserMessage,
      }"
    >
      <!-- Slider Message -->
      <MessageSlider
        v-if="isSliderMessage"
        :message="message"
        @sendPostback="$emit('sendPostback', $event)"
        :buttonOutline="buttonOutline"
      />
      <!-- Bubble Message -->
      <MessageBubble
        v-else-if="isTextMessage"
        :message="message"
        :is-user-message="isUserMessage"
        :button-outline="buttonOutline"
        :agents="agents"
        :should-disable-if-in-livechat="shouldDisableIfInLivechat"
        @sendPostback="$emit('sendPostback', $event)"
        @handleExpandVideo="$emit('handleExpandVideo', message)"
      />
      <!-- Grid -->
      <MessageGrid
        v-else-if="isGridMessage"
        :message="message"
        :should-disable-if-in-livechat="shouldDisableIfInLivechat"
        @sendPostback="$emit('sendPostback', $event)"
        @setLightBoxImage="$emit('setLightBoxImage', $event)"
      />
      <!--Image message-->
      <!--File message-->
      <MessageFile
        v-else-if="containSingleFileContent"
        :message="message"
        :is-user-message="isUserMessage"
        :button-outline="buttonOutline"
        @sendPostback="$emit('sendPostback', $event)"
        @setLightBoxImage="$emit('setLightBoxImage', $event)"
      />

      <!-- Image message from bot -->
      <!-- Carousel -->
      <MessageCarousel
        v-else-if="isCarouselMessage"
        :message="message"
        :button-outline="buttonOutline"
        :should-disable-if-in-livechat="shouldDisableIfInLivechat"
        @sendPostback="$emit('sendPostback', $event)"
        @setLightBoxImage="$emit('setLightBoxImage', $event)"
      />

      <!-- Compact -->
      <MessageCompact
        v-else
        :message="message"
        :button-outline="buttonOutline"
        :should-disable-if-in-livechat="shouldDisableIfInLivechat"
        @sendPostback="$emit('sendPostback', $event)"
        @setLightBoxImage="$emit('setLightBoxImage', $event)"
      />

      <!--Quick Replies-->
      <MessageQuickReplies
        v-if="shouldShowQuickReplies"
        :message="message"
        :should-disable-if-in-livechat="shouldDisableIfInLivechat"
        :button-outline="buttonOutline"
        @sendPostback="$emit('sendPostback', $event)"
      />
      <MessageSingPass v-if="showSingPassQR" :button-outline="buttonOutline" />
      <MessageScribe v-if="isScribeMessage" :button-outline="buttonOutline" />
    </div>
  </transition>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message
 * @description Central message dispatcher that routes to specialized message components (Vue 3 Composition API)
 */
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import * as _ from "lodash-es";
import "viewerjs/dist/viewer.css";

// Message sub-components for different content types
import MessageBubble from "./Bubble"; // Text messages with optional buttons
import MessageFile from "./File"; // Files, images, videos, audio
import MessageQuickReplies from "./QuickReplies"; // Tappable suggestion chips
import MessageCarousel from "./Carousel"; // Horizontal scrollable cards
import MessageCompact from "./Compact"; // Vertically stacked cards
import MessageGrid from "./Grid"; // Grid layout (beta)
import MessageSlider from "./Slider"; // Slider pagination (beta)
import MessageSingPass from "./Singpass"; // Singapore NDI auth QR
import MessageScribe from "./Scribe"; // AI transcription interface

import { useMainStore } from "@/stores/main";

/**
 * Component props
 */
const props = defineProps({
  /**
   * The message object containing type, data, and options
   */
  message: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Whether to show quick reply buttons (typically true for last message)
   */
  showQuickReply: {
    type: Boolean,
    default: false,
  },
  /**
   * List of available live chat agents for display
   */
  agents: {
    type: Array,
    default: () => [],
  },
  /**
   * Height offset for banner to prevent message overlap
   */
  maintainBannerHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Index of this message in the messages array
   */
  messageIndex: {
    type: Number,
    default: 0,
  },
});

/**
 * Component emits
 */
const emit = defineEmits(["sendPostback", "setLightBoxImage", "handleExpandVideo"]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Component local state
 */
const isStickyMenu = ref(false);
const authSession = ref(null);

/**
 * Computed getters from store
 */
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled);
const stickyMenu = computed(() => store.stickyMenu);
const settings = computed(() => store.settings);

/**
 * Determines if message should render as a simple text bubble
 *
 * A message is considered "text only" when:
 * 1. It has exactly one content item
 * 2. That content has no media attachments (images, files, videos, audio)
 * 3. It's not using compact list style
 *
 * This is the primary routing decision for MessageBubble component.
 */
const isTextMessage = computed(() => {
  const singleContent =
    props.message.data.content && props.message.data.content.length === 1;
  const images = _.get(props.message, "data.content[0].images");
  const files = _.get(props.message, "data.content[0].files");
  const videos = _.get(props.message, "data.content[0].videos");
  const audio = _.get(props.message, "data.content[0].audio");
  // validating images, files, videos, and audio item not null
  const isImageMessage =
    _.get(props.message, "data.content[0].image") ||
    (Array.isArray(images) && _.compact(images).length > 0);
  const isFileMessage = Array.isArray(files) && _.compact(files).length > 0;
  const isVideoMessage = Array.isArray(videos) && _.compact(videos).length > 0;
  const isAudioMessage = !!audio;
  const isListStyle = _.get(props.message, "data.listStyle") === "compact";

  return (
    singleContent &&
    !(isImageMessage || isFileMessage || isVideoMessage || isAudioMessage || isListStyle)
  );
});

/**
 * Determines if message should render as horizontal carousel
 *
 * Carousel is the default for multi-item messages when listStyle
 * is not explicitly set to "compact".
 */
const isCarouselMessage = computed(() => {
  return props.message.data.listStyle !== "compact";
});

/**
 * Determines if message should render as grid layout
 *
 * Grid mode is a beta feature that requires isUiBetaEnabled flag.
 * Set via message.options.mode = "grid"
 */
const isGridMessage = computed(() => {
  return props.message.options?.mode === "grid" && isUiBetaEnabled.value;
});

/**
 * Determines if message should render as slider/pagination
 *
 * Slider mode is a beta feature that requires isUiBetaEnabled flag.
 * Set via message.options.mode = "slider"
 */
const isSliderMessage = computed(() => {
  return props.message.options?.mode === "slider" && isUiBetaEnabled.value;
});

/**
 * Determines if message contains a single file/media item
 *
 * This handles messages that are purely media (image, file, video, audio)
 * without accompanying text or subtext. These route to MessageFile.
 */
const containSingleFileContent = computed(() => {
  return (
    props.message.data.content &&
    props.message.data.content.length === 1 &&
    (props.message.data.content[0].images ||
      props.message.data.content[0].image ||
      props.message.data.content[0].files ||
      props.message.data.content[0].audio ||
      props.message.data.content[0].videos) &&
    props.message.data.listStyle !== "compact" &&
    !_.has(props.message.data.content[0], "text") &&
    !_.has(props.message.data.content[0], "subtext")
  );
});

/**
 * Whether to disable postback buttons during live chat
 *
 * When live chat is active and disablePostbackDuringLivechat is enabled,
 * bot buttons should be disabled to prevent confusion.
 */
const shouldDisableIfInLivechat = computed(() => {
  return settings.value.disablePostbackDuringLivechat && settings.value.liveChatStatus;
});

/**
 * Check if message has any content to display
 *
 * Used to conditionally render the entire message container.
 * Empty messages are not rendered.
 */
const hasAnyContent = computed(() => {
  return !_.isEmpty(props.message.data.content);
});

/**
 * Whether to show quick reply buttons
 *
 * Quick replies are shown when:
 * 1. showQuickReply prop is true (usually for last message)
 * 2. Message has quickReplies data
 */
const shouldShowQuickReplies = computed(() => {
  return props.showQuickReply && !_.isEmpty(props.message.data.quickReplies);
});

/**
 * Determines if this message is from the user
 *
 * User messages ("message" or "postback") are right-aligned.
 * Bot/agent messages are left-aligned.
 */
const isUserMessage = computed(() => {
  return props.message.type === "message" || props.message.type === "postback";
});

/**
 * Maps settings button color to Element UI button type
 *
 * Converts color names to Element UI semantic button types
 * for consistent styling.
 */
const buttonOutline = computed(() => {
  switch (settings.value.buttonOutlineColor) {
    case "red":
      return "danger";
    case "blue":
      return "primary";
    case "green":
      return "success";
    default:
      return "primary";
  }
});

/**
 * CSS class for sticky menu positioning
 *
 * When a message is part of a sticky menu flow, applies
 * language-specific positioning class.
 */
const stickyMenuClass = computed(() => {
  if (isStickyMenu.value)
    return `sticky-menu-${stickyMenu.value[props.message.options.nodeId]}`;
  return null;
});

/**
 * Whether to show SingPass QR code component
 *
 * Handles Singapore NDI authentication QR display logic.
 */
const showSingPassQR = computed(() => {
  const isQr = props.message?.data?.content[0]?.options?.showQRCode;
  const hideQr = props.message?.data?.content[0]?.options?.disabledQR;
  const el = document.getElementById("ndi-qr");
  if (!el && isQr) return true;
  if (el && isQr) {
    // Side effect: scroll to QR code
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth" });
    }, 0);
  }
  if (el && hideQr) {
    // Side effect: remove QR element from DOM
    setTimeout(() => {
      el.remove();
    }, 0);
  }
  return false;
});

/**
 * Whether to show AI Scribe transcription interface
 */
const isScribeMessage = computed(() => {
  return props.message?.data?.content[0]?.options?.openScribe;
});

/**
 * Mounted lifecycle hook
 *
 * Sets up click handlers for preview images to open in lightbox.
 */
onMounted(() => {
  const images = document.querySelectorAll(".kr-preview-image");
  if (images.length > 0) {
    images.forEach((image) => {
      image.addEventListener("click", () => {
        emit("setLightBoxImage", image.src);
      });
    });
  }

  // Initialize sticky menu state if this message is part of a sticky menu flow
  if (!isUserMessage.value && _.has(stickyMenu.value, props.message.options?.nodeId)) {
    isStickyMenu.value = true;
    store.setStickyMenuLanguage(stickyMenu.value[props.message.options.nodeId]);
  }
});

/**
 * Before unmount lifecycle hook
 *
 * Cleans up Singapore NDI auth session if it was successful.
 * Prevents lingering authentication state.
 */
onBeforeUnmount(() => {
  if (authSession.value === "SUCCESSFUL") {
    window.NDI.cancelAuthSession();
  }
});
</script>

<style>
.has-banner-offset {
  margin-top: var(--banner-offset, 0);
}

.kr-preview-image {
  cursor: pointer;
}

.keyreply-read-more {
  cursor: pointer;
  font-size: 16px;
  margin-top: 5px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
